/*
/mcp/ page: copy buttons, client tabs, topic choice and live radio data.

Without JS the page is whole: every client is a <details> with its steps, numbers show
dashes, the chat shows only the question. JS turns the details into tabs (WAI-ARIA tabs
pattern: arrows, Home/End), swaps the topic's address into every setup from the catalog
the page carries (partials/mcp/connect.hbs, built from account.thehype.news/connect.json),
reads ?client=&topic= (old /connect links redirect here), fills the numbers, the topic
examples and the chat from the radio feeds, then plays the chat. Each feed loads and
fails on its own. A PJAX swap aborts fetches, timers and listeners.
*/

import playChat from './mcpChat.js';
import {
    CHAT_STORIES, DAY_MS, FEED_TOPICS, FEEDS_URL, WEEK_MS, ago, countWindow, coveredCount,
    dayLabel, episodes, fundingRounds, newsStories, roundLine, usableFeed,
} from './mcpFeeds.js';

const COPIED_MS = 2000;
const FETCH_TIMEOUT_MS = 10_000;
const CATALOG_URL = 'https://account.thehype.news/connect.json';

let teardown = null;

export default function mcpPage() {
    const page = document.querySelector('[data-mcp-page]');

    // bfcache brings the page back alive — keep it.
    if (page?.dataset.mcpReady) {
        return;
    }
    teardown?.();
    teardown = null;
    if (!page) {
        return;
    }
    page.dataset.mcpReady = 'true';
    const lifetime = new AbortController();

    teardown = () => lifetime.abort();
    enableCopy(page, lifetime.signal);
    const catalog = readCatalog(page);
    const setups = catalog && enableSetups(page, catalog, lifetime.signal);

    applyQuery(setups);
    if (setups) {
        followClientLinks(page, setups, lifetime.signal);
    }
    loadLive(page, lifetime.signal);
    if (catalog) {
        warnIfCatalogChanged(catalog.data_hash, lifetime.signal);
    }
}

function enableCopy(page, signal) {
    const status = page.querySelector('[data-mcp-status]');

    if (!navigator.clipboard) {
        return;
    }
    for (const button of page.querySelectorAll('[data-mcp-copy]')) {
        button.hidden = false;
    }
    page.addEventListener('click', (event) => {
        const button = event.target.closest('[data-mcp-copy]');

        if (!button) {
            return;
        }
        const source = button.parentElement.querySelector('code');

        navigator.clipboard.writeText(source.textContent).then(
            () => announce(button, status, 'Copied'),
            () => announce(button, status, 'Select and copy'),
        );
    }, { signal });
}

function announce(button, status, message) {
    const label = button.querySelector('span');
    const before = label?.textContent;

    status.textContent = message;
    if (label) {
        label.textContent = message;
        window.setTimeout(() => {
            label.textContent = before;
        }, COPIED_MS);
    }
}

function readCatalog(page) {
    const script = page.querySelector('[data-mcp-catalog]');

    try {
        return script ? JSON.parse(script.textContent) : null;
    } catch {
        return null;
    }
}

// Tabs over the client details and the topic picker; returns the controls for ?query.
function enableSetups(page, catalog, signal) {
    const list = page.querySelector('[data-mcp-clients]');
    const setups = [...list.querySelectorAll('[data-mcp-setup]')];
    const tabs = buildTabs(setups);
    const picker = page.querySelector('[data-mcp-topic-picker]');
    const controls = {
        select: id => selectTab(tabs, setups, id),
        topic: id => applyTopic(page, catalog, id),
    };

    list.before(tabs.row);
    list.dataset.tabs = '';
    tabs.row.addEventListener('click', (event) => {
        const tab = event.target.closest('[role="tab"]');

        if (tab) {
            controls.select(tab.dataset.client);
        }
    }, { signal });
    tabs.row.addEventListener('keydown', event => moveFocus(event, tabs, controls), { signal });
    picker.hidden = false;
    picker.addEventListener('click', (event) => {
        const choice = event.target.closest('[data-mcp-topic-choice]');

        if (choice) {
            controls.topic(choice.dataset.mcpTopicChoice);
        }
    }, { signal });
    controls.select(setups[0].dataset.mcpSetup);

    return controls;
}

function buildTabs(setups) {
    const row = document.createElement('div');

    row.className = 'th-mcp__tabs';
    row.setAttribute('role', 'tablist');
    row.setAttribute('aria-label', 'Your assistant');
    const buttons = setups.map((setup) => {
        const id = setup.dataset.mcpSetup;
        const body = setup.querySelector('.th-mcp__setup-body');
        const tab = document.createElement('button');

        tab.type = 'button';
        tab.className = 'th-mcp__tab';
        tab.id = `th-mcp-tab-${id}`;
        tab.dataset.client = id;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', body.id = `th-mcp-panel-${id}`);
        tab.append(...[...setup.querySelector('summary').childNodes].map(node => node.cloneNode(true)));
        body.setAttribute('role', 'tabpanel');
        body.setAttribute('aria-labelledby', tab.id);
        body.tabIndex = 0;
        setup.open = true;
        row.append(tab);

        return tab;
    });

    return { row, buttons };
}

function selectTab(tabs, setups, id) {
    const known = setups.some(setup => setup.dataset.mcpSetup === id);
    const chosen = known ? id : setups[0].dataset.mcpSetup;

    for (const tab of tabs.buttons) {
        const selected = tab.dataset.client === chosen;

        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
    }
    for (const setup of setups) {
        setup.hidden = setup.dataset.mcpSetup !== chosen;
    }
}

function moveFocus(event, tabs, controls) {
    const index = tabs.buttons.indexOf(document.activeElement);
    const last = tabs.buttons.length - 1;
    const targets = {
        ArrowRight: index === last ? 0 : index + 1,
        ArrowLeft: index === 0 ? last : index - 1,
        Home: 0,
        End: last,
    };

    if (index === -1 || !(event.key in targets)) {
        return;
    }
    event.preventDefault();
    const tab = tabs.buttons[targets[event.key]];

    controls.select(tab.dataset.client);
    tab.focus();
}

// Every setup shows the chosen topic's config, install link and agent message.
function applyTopic(page, catalog, topic) {
    for (const choice of page.querySelectorAll('[data-mcp-topic-choice]')) {
        choice.setAttribute('aria-pressed', String(choice.dataset.mcpTopicChoice === topic));
    }
    for (const setup of page.querySelectorAll('[data-mcp-setup]')) {
        const data = catalog.clients[setup.dataset.mcpSetup]?.[topic];

        if (data) {
            fillSetup(setup, data);
        }
    }
}

function fillSetup(setup, data) {
    for (const field of setup.querySelectorAll('[data-mcp-field]')) {
        const name = field.dataset.mcpField;

        if (name === 'button' && data.button) {
            field.href = data.button.href;
        } else if (name === 'config' || name === 'prompt') {
            field.textContent = data[name];
        }
    }
}

// Hero buttons without an install link point at #connect-<client>: with tabs that
// details is hidden, so open its tab and scroll to the connect section.
function followClientLinks(page, controls, signal) {
    page.addEventListener('click', (event) => {
        const link = event.target.closest('a[href^="#connect-"]');
        const modified = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey
            || event.altKey;

        if (!link || modified) {
            return;
        }
        event.preventDefault();
        controls.select(link.getAttribute('href').slice('#connect-'.length));
        page.querySelector('#connect').scrollIntoView();
    }, { signal });
}

// ?client=&topic= (old /connect links) or #connect-<client> (a shared tab link).
function applyQuery(controls) {
    const query = new URLSearchParams(window.location.search);
    const hash = window.location.hash.match(/^#connect-([a-z-]+)$/);

    if (!controls) {
        return;
    }
    if (hash) {
        controls.select(hash[1]);
    }
    if (query.has('topic')) {
        controls.topic(query.get('topic'));
    }
    if (query.has('client')) {
        controls.select(query.get('client'));
    }
}

// --- live data ---

function loadLive(page, signal) {
    const view = { page, signal };

    for (const topic of FEED_TOPICS) {
        loadFeed(topic, signal)
            .then(doc => doc && showFeed(view, topic, doc))
            .catch((error) => {
                if (!signal.aborted) {
                    console.warn(`mcp page: ${topic} feed`, error);
                }
            });
    }
}

async function loadFeed(topic, signal) {
    const response = await fetch(`${FEEDS_URL}/${topic}.json`, {
        signal: withTimeout(signal),
    });

    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    const doc = await response.json();

    return usableFeed(doc, topic, Date.now()) ? doc : null;
}

// The page's lifetime plus a timeout; browsers without AbortSignal.any get the lifetime.
function withTimeout(signal) {
    if (!AbortSignal.any) {
        return signal;
    }

    return AbortSignal.any([signal, AbortSignal.timeout(FETCH_TIMEOUT_MS)]);
}

// view: { page, signal } — the page and its lifetime.
function showFeed(view, topic, doc) {
    if (view.signal.aborted) {
        return;
    }
    if (topic === 'news') {
        showNews(view, doc);
    } else if (topic === 'funding') {
        showFunding(view.page, doc);
    } else {
        showEpisodes(view.page, doc);
    }
}

function showNews({ page, signal }, doc) {
    const stories = newsStories(doc);

    if (stories.length === 0) {
        return;
    }
    setStat(page, 'last', ago(stories[0].aired, Date.now()));
    setStat(page, 'stories', coveredCount(stories.map(story => story.aired), doc.built_at, DAY_MS));
    setExample(page, 'news', { when: dayLabel(stories[0].aired), line: stories[0].title });
    setAnswer(page, 'news', stories.slice(0, 2).map(story => story.title).join(' · '));
    fillChat(page, stories.slice(0, CHAT_STORIES), signal);
}

function showFunding(page, doc) {
    const rounds = fundingRounds(doc);

    if (rounds.length === 0) {
        return;
    }
    // The funding export is a 7-day window by contract (thehype-mcp topics_funding).
    setStat(page, 'rounds', countWindow(rounds.map(round => round.seen), doc.built_at, WEEK_MS));
    setExample(page, 'funding', {
        when: `${dayLabel(rounds[0].seen)} · new round`,
        line: roundLine(rounds[0]),
    });
    setAnswer(page, 'funding', rounds.slice(0, 2).map(roundLine).join(' · '));
}

function showEpisodes(page, doc) {
    const list = episodes(doc);

    if (list.length === 0) {
        return;
    }
    setStat(page, 'episodes', list.length);
    setExample(page, 'ai-morning', { when: `Episode ${list[0].number}`, line: list[0].title });
    setAnswer(page, 'ai-morning', list[0].title);
}

function setStat(page, name, value) {
    const cell = page.querySelector(`[data-mcp-stat="${name}"]`);

    // null: the feed cannot answer honestly — the dash stays.
    if (cell && value !== null) {
        cell.textContent = String(value);
    }
}

function setExample(page, topic, { when, line }) {
    const slot = page.querySelector(`[data-mcp-example="${topic}"]`);
    const label = document.createElement('span');

    if (!slot) {
        return;
    }
    label.className = 'th-mcp__example-when';
    label.textContent = when;
    slot.replaceChildren(label, document.createTextNode(line));
}

function setAnswer(page, topic, line) {
    const slot = page.querySelector(`[data-mcp-answer-for="${topic}"]`);

    if (slot) {
        slot.textContent = line;
    }
}

function fillChat(page, stories, signal) {
    const chat = page.querySelector('[data-mcp-chat]');
    const answer = chat?.querySelector('[data-mcp-answer]');

    if (!answer) {
        return;
    }
    const date = document.createElement('time');

    date.dateTime = new Date(stories[0].aired).toISOString().slice(0, 10);
    date.textContent = dayLabel(stories[0].aired);
    answer.querySelector('[data-mcp-answer-date]').replaceChildren(date);
    answer.querySelector('[data-mcp-stories]').replaceChildren(...stories.map(storyItem));
    answer.hidden = false;
    playChat(chat, signal);
}

function storyItem(story) {
    const item = document.createElement('li');
    const title = document.createElement('strong');

    title.textContent = story.title;
    item.append(title);
    if (story.line) {
        item.append(` — ${story.line}`);
    }

    return item;
}

// The page carries a build-time copy of the catalog; tell the console when it is stale.
async function warnIfCatalogChanged(hash, signal) {
    try {
        const response = await fetch(CATALOG_URL, { signal });
        const live = await response.json();

        if (live.data_hash !== hash) {
            console.warn('mcp page: connect.json changed — run npm run sync-mcp and redeploy');
        }
    } catch (error) {
        if (!signal.aborted) {
            console.warn('mcp page: connect.json unavailable', error);
        }
    }
}
